import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { SubscriptionPlan } from '@/components/pricing/SubscriptionPlan';
import RequestItemFormPopup from '@/components/requestItem/RequestItemFormPopup';

export default function Features() {
  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);

  const handleStartSearch = () => {
    setIsRequestFormOpen(true);
  };

  const handleCloseRequestForm = () => {
    setIsRequestFormOpen(false);
  };

  return (
    <>
      <Head>
        <title>Car Spares Made Simple | Scan, Snap, Select, Done - CarSparesEzyFind</title>
        <meta name="description" content="South Africa's #1 car spares marketplace. AI-powered search, verified suppliers, reverse bidding, and 24/7 support. Find any part in minutes with our Scan, Snap, Select, Done process." />
        <meta name="keywords" content="car spares, auto parts, scan license disc, upload part photo, RFQ, reverse bidding, buy auto parts, hire mechanic, vehicle services, eCommerce, mobile app, South Africa, used car parts, new car parts, vehicle license renewal, change of ownership, traffic fines, tow truck, lawyer, car insurance, extended warranty, AI search, verified suppliers" />
      </Head>
      <main className="bg-gradient-to-br from-blue-50 to-gray-100 min-h-screen py-10 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-extrabold text-blue-900 mb-4">Car Spares Made Simple</h1>
            <p className="text-xl text-gray-700 mb-6">
              South Africa's #1 AI-Powered Car Spares Marketplace
            </p>
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-8 py-4 rounded-full inline-block mb-8">
              <span className="text-2xl font-bold">Scan • Snap • Select • Done</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-green-100 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-800">500+</div>
                <div className="text-green-700">Verified Suppliers</div>
              </div>
              <div className="bg-blue-100 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-800">1M+</div>
                <div className="text-blue-700">Parts Available</div>
              </div>
              <div className="bg-purple-100 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-800">30 Sec</div>
                <div className="text-purple-700">Average Quote Time</div>
              </div>
              <div className="bg-orange-100 p-4 rounded-lg">
                <div className="text-2xl font-bold text-orange-800">24/7</div>
                <div className="text-orange-700">Customer Support</div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Competitive Advantages */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-blue-600">
              <h2 className="text-3xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="bg-blue-100 p-2 rounded-full mr-3">🏆</span>
                Why CarSparesEzyFind Beats the Competition
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-semibold">UNIQUE</span>
                    <div>
                      <h3 className="font-bold text-blue-700">AI-Powered Part Recognition</h3>
                      <p className="text-gray-600">Our advanced AI instantly identifies parts from photos - no competitor offers this technology</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-semibold">EXCLUSIVE</span>
                    <div>
                      <h3 className="font-bold text-blue-700">Reverse Bidding System</h3>
                      <p className="text-gray-600">Suppliers compete for your business - guaranteed lowest prices unlike fixed-price competitors</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-semibold">VERIFIED</span>
                    <div>
                      <h3 className="font-bold text-blue-700">100% Verified Suppliers</h3>
                      <p className="text-gray-600">Every supplier is business-verified and rated - other platforms allow unverified sellers</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-semibold">FASTEST</span>
                    <div>
                      <h3 className="font-bold text-blue-700">30-Second Quote Generation</h3>
                      <p className="text-gray-600">Get quotes in seconds, not days like traditional spare shops</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-semibold">COMPLETE</span>
                    <div>
                      <h3 className="font-bold text-blue-700">All-in-One Vehicle Services</h3>
                      <p className="text-gray-600">Beyond spares: license renewal, insurance, legal help - no competitor offers this range</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-semibold">SECURE</span>
                    <div>
                      <h3 className="font-bold text-blue-700">Bank-Grade Security</h3>
                      <p className="text-gray-600">SSL encryption, secure payments, and fraud protection - many competitors lack this security</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Trust & Safety */}
            <section className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow-lg p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="bg-green-100 p-2 rounded-full mr-3">🛡️</span>
                Your Trust & Safety is Our Priority
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-3xl mb-3">✅</div>
                  <h3 className="font-bold text-green-700 mb-2">Verified Business Partners</h3>
                  <p className="text-gray-600">Every supplier undergoes strict business verification, insurance checks, and background screening</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-3xl mb-3">🔒</div>
                  <h3 className="font-bold text-blue-700 mb-2">Secure Transactions</h3>
                  <p className="text-gray-600">Bank-grade encryption, PCI DSS compliance, and secure escrow payment system</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="text-3xl mb-3">⭐</div>
                  <h3 className="font-bold text-purple-700 mb-2">Quality Guarantee</h3>
                  <p className="text-gray-600">Money-back guarantee, quality assurance, and comprehensive warranty protection</p>
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-purple-600">
              <h2 className="text-3xl font-bold text-purple-800 mb-6 flex items-center">
                <span className="bg-purple-100 p-2 rounded-full mr-3">⚡</span>
                How CarSparesEzyFind Works (Simple 4-Step Process)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-800">1</span>
                  </div>
                  <h3 className="font-bold text-blue-700 mb-2">📱 SCAN</h3>
                  <p className="text-gray-600">Scan your license disc or upload part photos using our AI-powered recognition</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-green-800">2</span>
                  </div>
                  <h3 className="font-bold text-green-700 mb-2">📸 SNAP</h3>
                  <p className="text-gray-600">Our AI instantly identifies your vehicle and required parts with 99% accuracy</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-purple-800">3</span>
                  </div>
                  <h3 className="font-bold text-purple-700 mb-2">🔍 SELECT</h3>
                  <p className="text-gray-600">Choose from competitive quotes from verified suppliers in your area</p>
                </div>
                <div className="text-center">
                  <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-orange-800">4</span>
                  </div>
                  <h3 className="font-bold text-orange-700 mb-2">✅ DONE</h3>
                  <p className="text-gray-600">Secure payment, real-time tracking, and quality guaranteed delivery</p>
                </div>
              </div>
            </section>

            {/* Complete Service Offering */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-orange-600">
              <h2 className="text-3xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="bg-orange-100 p-2 rounded-full mr-3">🚗</span>
                Complete Vehicle Ecosystem - Beyond Just Spares
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-bold text-blue-700 mb-2">🔧 Parts & Accessories</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• New & Used OEM Parts</li>
                    <li>• Aftermarket Accessories</li>
                    <li>• Performance Upgrades</li>
                    <li>• Tyres & Batteries</li>
                  </ul>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-bold text-green-700 mb-2">🔧 Professional Services</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Mobile Mechanics</li>
                    <li>• Towing Services</li>
                    <li>• Roadside Assistance</li>
                    <li>• Vehicle Inspections</li>
                  </ul>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-bold text-purple-700 mb-2">📋 Legal & Admin</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• License Renewals</li>
                    <li>• Change of Ownership</li>
                    <li>• Traffic Fine Payments</li>
                    <li>• Legal Assistance</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-bold text-yellow-700 mb-2">🛡️ Insurance & Protection</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Car Insurance Quotes</li>
                    <li>• Extended Warranties</li>
                    <li>• Roadside Cover</li>
                    <li>• Gap Insurance</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-bold text-red-700 mb-2">🚨 Emergency Services</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 24/7 Breakdown Assist</li>
                    <li>• Emergency Towing</li>
                    <li>• Accident Support</li>
                    <li>• Legal Roadblock Help</li>
                  </ul>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-bold text-indigo-700 mb-2">📱 Digital Services</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Vehicle History Reports</li>
                    <li>• Maintenance Reminders</li>
                    <li>• Digital Logbook</li>
                    <li>• Fleet Management</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Customer Trust & Reviews */}
            <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold mb-6 text-center">Trusted by 50,000+ South African Drivers & Taxi Owners</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur">
                  <div className="text-yellow-400 text-xl mb-2">⭐⭐⭐⭐⭐</div>
                  <p className="mb-3">"Found my BMW part in 5 minutes! Much faster than calling 20 shops."</p>
                  <div className="text-sm opacity-80">- Johan M., Cape Town</div>
                </div>
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur">
                  <div className="text-yellow-400 text-xl mb-2">⭐⭐⭐⭐⭐</div>
                  <p className="mb-3">"Saved R500 on my Toyota service thanks to their bidding system."</p>
                  <div className="text-sm opacity-80">- Sarah L., Johannesburg</div>
                </div>
                <div className="bg-white/10 p-6 rounded-lg backdrop-blur">
                  <div className="text-yellow-400 text-xl mb-2">⭐⭐⭐⭐⭐</div>
                  <p className="mb-3">"Professional service, verified suppliers, and great support team."</p>
                  <div className="text-sm opacity-80">- Mike D., Durban</div>
                </div>
              </div>
            </section>
            
            {/* Complete Service Offering */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-orange-600">
              <h2 className="text-3xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="bg-orange-100 p-2 rounded-full mr-3">🚗</span>
                Complete Auto Products & Services - Purchase, Bid, or Hire
              </h2>

              {/* Purchase Section */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-blue-800 mb-6 flex items-center">
                  <span className="bg-blue-100 p-2 rounded-full mr-3">🛒</span>
                  PURCHASE - Buy Auto Parts & Products Online
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-700 mb-3">🔧 New Auto Spares (Spare Shops)</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Brand New Parts</li>
                      <li>• Latest Models</li>
                      <li>• Full Warranty</li>
                      <li>• OEM Quality</li>
                      <li>• Immediate Delivery</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-bold text-green-700 mb-3">🏭 OEM Parts (Dealerships)</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Original Equipment</li>
                      <li>• Manufacturer Approved</li>
                      <li>• Perfect Fit Guarantee</li>
                      <li>• Premium Quality</li>
                      <li>• Dealership Standard</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-bold text-yellow-700 mb-3">♻️ Used - 2nd Hand Spares (Scrap Yards)</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Quality Tested</li>
                      <li>• Affordable Prices</li>
                      <li>• Inspected Parts</li>
                      <li>• Good Condition</li>
                      <li>• Budget Friendly</li>
                    </ul>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-bold text-purple-700 mb-3">🏴‍☠️ Pirate Spares</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Compatible Parts</li>
                      <li>• Budget Options</li>
                      <li>• Wide Selection</li>
                      <li>• Quick Fixes</li>
                      <li>• Emergency Solutions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Vehicle Manufacturers for SEO */}
              <div className="mb-12 bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                  🏭 Supported Vehicle Manufacturers & Dealerships
                </h3>
                <p className="text-center text-gray-600 mb-6">Get genuine parts directly from authorized dealerships and manufacturers</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">BMW</strong><br/>
                    <span className="text-gray-600">German Engineering</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Mercedes-Benz</strong><br/>
                    <span className="text-gray-600">Luxury Parts</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Volkswagen</strong><br/>
                    <span className="text-gray-600">VW Group</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Toyota</strong><br/>
                    <span className="text-gray-600">Reliable Parts</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Ford</strong><br/>
                    <span className="text-gray-600">American Made</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Haval</strong><br/>
                    <span className="text-gray-600">Chinese Quality</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Audi</strong><br/>
                    <span className="text-gray-600">Premium Parts</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Nissan</strong><br/>
                    <span className="text-gray-600">Japanese Tech</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Honda</strong><br/>
                    <span className="text-gray-600">Engineering</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Hyundai</strong><br/>
                    <span className="text-gray-600">Korean Quality</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Kia</strong><br/>
                    <span className="text-gray-600">Modern Design</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Mazda</strong><br/>
                    <span className="text-gray-600">Zoom Zoom</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Chevrolet</strong><br/>
                    <span className="text-gray-600">GM Quality</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Isuzu</strong><br/>
                    <span className="text-gray-600">Commercial</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Mitsubishi</strong><br/>
                    <span className="text-gray-600">Diamond Star</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Renault</strong><br/>
                    <span className="text-gray-600">French Flair</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Peugeot</strong><br/>
                    <span className="text-gray-600">Motion & Style</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Opel</strong><br/>
                    <span className="text-gray-600">German Design</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Suzuki</strong><br/>
                    <span className="text-gray-600">Small Wonder</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Subaru</strong><br/>
                    <span className="text-gray-600">Boxer Engine</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Volvo</strong><br/>
                    <span className="text-gray-600">Swedish Safety</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Jaguar</strong><br/>
                    <span className="text-gray-600">British Luxury</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Land Rover</strong><br/>
                    <span className="text-gray-600">Off-Road</span>
                  </div>
                  <div className="bg-white p-3 rounded border text-center">
                    <strong className="text-blue-700">Jeep</strong><br/>
                    <span className="text-gray-600">Adventure</span>
                  </div>
                </div>
              </div>

              {/* Bidding Section */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-purple-800 mb-6 flex items-center">
                  <span className="bg-purple-100 p-2 rounded-full mr-3">🏆</span>
                  BID - Reverse Bidding & Live Negotiations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                    <h4 className="font-bold text-purple-700 mb-3">📝 Submit RFQ (Request for Quote)</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Upload part photos/descriptions</li>
                      <li>• Set your budget range</li>
                      <li>• Choose delivery location</li>
                      <li>• Receive multiple quotes</li>
                      <li>• Compare supplier offers</li>
                    </ul>
                    <button className="mt-4 w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition-colors">
                      Start RFQ Process
                    </button>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                    <h4 className="font-bold text-green-700 mb-3">💬 Live Chat Negotiation</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Real-time chat with suppliers</li>
                      <li>• Negotiate prices instantly</li>
                      <li>• Ask technical questions</li>
                      <li>• Verify part compatibility</li>
                      <li>• Secure deal terms</li>
                    </ul>
                    <div className="mt-4 bg-green-600 text-white py-2 px-4 rounded text-center">
                      <span className="text-sm">🟢 Live Support Available</span>
                    </div>
                  </div>
                  <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                    <h4 className="font-bold text-orange-700 mb-3">🔥 Scrapyard Auctions</h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li>• Bid on used vehicle parts</li>
                      <li>• Whole vehicle auctions</li>
                      <li>• Engine & gearbox lots</li>
                      <li>• Accident damaged vehicles</li>
                      <li>• Bulk spare packages</li>
                    </ul>
                    <button className="mt-4 w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition-colors">
                      View Live Auctions
                    </button>
                  </div>
                </div>

                {/* Bidding Controls */}
                <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-4">🎯 Advanced Bidding Controls</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded border">
                      <h5 className="font-semibold text-blue-700 mb-2">⏰ Timeline Bidding</h5>
                      <p className="text-sm text-gray-600">Set auction end times, countdown timers, and auto-bid extensions</p>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <h5 className="font-semibold text-green-700 mb-2">💰 Price Cap Bidding</h5>
                      <p className="text-sm text-gray-600">Set maximum spend limits, reserve prices, and budget controls</p>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <h5 className="font-semibold text-purple-700 mb-2">📦 Quantity Bidding</h5>
                      <p className="text-sm text-gray-600">Bid on bulk quantities, partial lots, and stock availability caps</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hire Services Section */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-green-800 mb-6 flex items-center">
                  <span className="bg-green-100 p-2 rounded-full mr-3">👨‍🔧</span>
                  HIRE - Professional Automotive Services
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-700 mb-3">🔧 Mechanical Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Mobile Mechanics</li>
                      <li>• Engine Diagnostics</li>
                      <li>• Brake Services</li>
                      <li>• Clutch Repairs</li>
                      <li>• Transmission Service</li>
                      <li>• Electrical Repairs</li>
                      <li>• Air Conditioning</li>
                      <li>• Suspension Work</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-bold text-green-700 mb-3">🛠️ Specialized Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Disc Skimming</li>
                      <li>• Wheel Alignment</li>
                      <li>• Tyre Fitting</li>
                      <li>• Battery Replacement</li>
                      <li>• Oil Changes</li>
                      <li>• Exhaust Repairs</li>
                      <li>• Windscreen Replacement</li>
                      <li>• Paint & Body Work</li>
                    </ul>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-bold text-purple-700 mb-3">🚨 Emergency Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 24/7 Roadside Assistance</li>
                      <li>• Emergency Towing</li>
                      <li>• Battery Jump Start</li>
                      <li>• Lockout Services</li>
                      <li>• Tyre Changes</li>
                      <li>• Fuel Delivery</li>
                      <li>• Accident Recovery</li>
                      <li>• Breakdown Assistance</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-bold text-yellow-700 mb-3">✨ Cosmetic Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Car Wash & Detailing</li>
                      <li>• Interior Cleaning</li>
                      <li>• Paint Protection</li>
                      <li>• Waxing & Polishing</li>
                      <li>• Upholstery Cleaning</li>
                      <li>• Chrome Polishing</li>
                      <li>• Leather Treatment</li>
                      <li>• Odor Removal</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-bold text-red-700 mb-3">🏁 Performance Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• ECU Tuning</li>
                      <li>• Performance Upgrades</li>
                      <li>• Turbo Services</li>
                      <li>• Exhaust Modifications</li>
                      <li>• Suspension Tuning</li>
                      <li>• Brake Upgrades</li>
                      <li>• Chip Tuning</li>
                      <li>• Dyno Testing</li>
                    </ul>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <h4 className="font-bold text-indigo-700 mb-3">📋 Inspection & Certification</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Roadworthy Certificates</li>
                      <li>• Vehicle Inspections</li>
                      <li>• Pre-Purchase Checks</li>
                      <li>• Compliance Testing</li>
                      <li>• Safety Inspections</li>
                      <li>• Emissions Testing</li>
                      <li>• Insurance Assessments</li>
                      <li>• Technical Reports</li>
                    </ul>
                  </div>
                </div>

                {/* Service Booking */}
                <div className="mt-6 bg-gradient-to-r from-green-100 to-blue-100 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-4 text-center">🎯 How to Book Services</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-white font-bold">1</span>
                      </div>
                      <h5 className="font-semibold text-green-700">Select Service</h5>
                      <p className="text-sm text-gray-600">Choose from our service menu</p>
                    </div>
                    <div className="text-center">
                      <div className="bg-blue-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-white font-bold">2</span>
                      </div>
                      <h5 className="font-semibold text-blue-700">Compare Quotes</h5>
                      <p className="text-sm text-gray-600">Get quotes from local providers</p>
                    </div>
                    <div className="text-center">
                      <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-white font-bold">3</span>
                      </div>
                      <h5 className="font-semibold text-purple-700">Book Appointment</h5>
                      <p className="text-sm text-gray-600">Schedule at your convenience</p>
                    </div>
                    <div className="text-center">
                      <div className="bg-orange-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-white font-bold">4</span>
                      </div>
                      <h5 className="font-semibold text-orange-700">Track Progress</h5>
                      <p className="text-sm text-gray-600">Real-time service updates</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>


            {/* Mobile Apps & Accessibility */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-green-600">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="bg-green-100 p-2 rounded-full mr-3">📱</span>
                Download Our Award-Winning Mobile App
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-blue-700 mb-4">Available on All Platforms</h3>
                  <p className="text-gray-700 mb-6">
                    Access CarSparesEzyFind anywhere, anytime. Our mobile app offers the full website experience 
                    with added features like offline browsing, push notifications, and GPS-based supplier matching.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Offline part browsing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Real-time order tracking</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>Push notifications for quotes</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600">✓</span>
                      <span>GPS supplier location</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-gray-700 mb-4">
                    Join <strong>50,000+</strong> users on mobile. Download from your preferred app store:
                  </p>
                  <div className="flex flex-col space-y-4 items-center">
                    <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank" className="hover:scale-105 transition-transform">
                      <Image src={'/images/app-store-btn.webp'} width={200} height={59} alt="Download on App Store" />
                    </Link>
                    <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank" className="hover:scale-105 transition-transform">
                      <Image src={'/images/google-play-btn.webp'} width={200} height={59} alt="Get it on Google Play" />
                    </Link>
                    <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank" className="hover:scale-105 transition-transform">
                      <Image src={'/images/app-gallery-btn.webp'} width={200} height={59} alt="Explore it on AppGallery" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-4xl font-bold mb-4">Ready to Experience the Difference?</h2>
              <p className="text-xl mb-6">Join thousands of satisfied customers who chose CarSparesEzyFind over the competition</p>
              <div className="space-y-4">
                <button 
                  onClick={handleStartSearch}
                  className="bg-white text-orange-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  Start Your Search Now - It's FREE!
                </button>
                <p className="text-sm opacity-90">No registration required to browse • Instant quotes • Secure payments</p>
              </div>
            </section>

            {/* Business Partnership Section */}
            <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 text-white rounded-xl shadow-2xl p-8 border border-purple-200">
              <div className="max-w-4xl mx-auto text-center">
                {/* Icon and Badge */}
                <div className="mb-6">
                  <div className="bg-white/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur">
                    <span className="text-4xl">🚀</span>
                  </div>
                  <span className="bg-yellow-400 text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                    Business Opportunity
                  </span>
                </div>

                {/* Main Heading */}
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight">
                  Do You Own a{' '}
                  <span className="text-yellow-300">Spare Shop</span>,{' '}
                  <span className="text-green-300">Scrapyard</span>, or{' '}
                  <span className="text-blue-300">Automotive Business</span>?
                </h2>

                {/* Subheading with emphasis */}
                <div className="bg-white/10 backdrop-blur rounded-lg p-6 mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-yellow-300 mb-2">
                    We Help You Grow It!
                  </h3>
                  <p className="text-lg text-white/90">
                    Join South Africa's largest automotive marketplace and reach 50,000+ potential customers
                  </p>
                </div>

                {/* Benefits Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white/10 p-4 rounded-lg backdrop-blur">
                    <div className="text-2xl mb-2">📈</div>
                    <h4 className="font-bold text-yellow-300">Increase Sales</h4>
                    <p className="text-sm text-white/80">Get more customers instantly</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg backdrop-blur">
                    <div className="text-2xl mb-2">🎯</div>
                    <h4 className="font-bold text-green-300">Targeted Leads</h4>
                    <p className="text-sm text-white/80">Customers come to you</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg backdrop-blur">
                    <div className="text-2xl mb-2">💰</div>
                    <h4 className="font-bold text-blue-300">Higher Profits</h4>
                    <p className="text-sm text-white/80">Competitive bidding system</p>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="space-y-4">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    Register Today & Start Growing! 🚀
                  </h3>
                  <p className="text-lg text-white/90 mb-6">
                    Choose the perfect subscription plan for your business needs
                  </p>
                  
                  {/* Subscription Plan Button/Section */}
                  <div className="bg-white/20 backdrop-blur rounded-lg p-6">
                    <h4 className="text-xl font-bold text-yellow-300 mb-4 flex items-center justify-center">
                      <span className="mr-2">⬇️</span>
                      Select Your Subscription Plan Below
                      <span className="ml-2">⬇️</span>
                    </h4>
                  </div>
                </div>
              </div>
            </section>

            {/* Subscription Plan Component */}
            <section className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
              <SubscriptionPlan />
            </section>
          </div>
        </div>
      </main>

      {/* Request Item Form Popup */}
      {isRequestFormOpen && (
        <RequestItemFormPopup onClose={handleCloseRequestForm} />
      )}
    </>
  );
}